import type { FunctionComponent } from 'preact'
import type { AIConversationContext, ContextualField } from '@/types/aiChat'
import { WebsterIcon } from './WebsterIcon'

interface ChatHeaderProps {
  context: AIConversationContext | null
  onClose: () => void
  onMinimize: () => void
  onExpand: () => void
  isMinimized: boolean
  onSnapLeft?: () => void
  onSnapRight?: () => void
}

// --- Sub-components ---

const SnapButton: FunctionComponent<{
  direction: 'left' | 'right'
  onClick: () => void
}> = ({ direction, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="p-1.5 hover:bg-gray-200 dark:hover:bg-slate-600 rounded transition-colors cursor-pointer"
    aria-label={`Snap to ${direction === 'left' ? 'Left' : 'Right'}`}
    title={`Move to ${direction} side`}
  >
    <svg className="w-4 h-4 text-gray-600 dark:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      {direction === 'left' ? (
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
      ) : (
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
      )}
    </svg>
  </button>
)

const WindowControls: FunctionComponent<{
  isMinimized: boolean
  onMinimize: () => void
  onExpand: () => void
  onClose: () => void
  onSnapLeft?: () => void
  onSnapRight?: () => void
}> = ({ isMinimized, onMinimize, onExpand, onClose, onSnapLeft, onSnapRight }) => (
  <div className="flex items-center gap-1">
    {!isMinimized && onSnapLeft && <SnapButton direction="left" onClick={onSnapLeft} />}
    {!isMinimized && onSnapRight && <SnapButton direction="right" onClick={onSnapRight} />}

    {/* Minimize/Expand button */}
    <button
      type="button"
      onClick={isMinimized ? onExpand : onMinimize}
      className="p-1.5 hover:bg-gray-200 dark:hover:bg-slate-600 rounded transition-colors cursor-pointer"
      aria-label={isMinimized ? "Expand" : "Minimize"}
      title={isMinimized ? "Expand" : "Minimize"}
    >
      {isMinimized ? (
        <svg className="w-4 h-4 text-gray-600 dark:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
        </svg>
      ) : (
        <svg className="w-4 h-4 text-gray-600 dark:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
        </svg>
      )}
    </button>
    <button
      type="button"
      onClick={onClose}
      className="p-1.5 hover:bg-gray-200 dark:hover:bg-slate-600 rounded transition-colors cursor-pointer"
      aria-label="Close"
    >
      <svg className="w-4 h-4 text-gray-600 dark:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  </div>
)

const EditCapabilities: FunctionComponent<{ fields: ContextualField[] }> = ({ fields }) => (
  <div className="flex items-start gap-2 p-2 bg-green-50 rounded-lg border border-green-200">
    <svg className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
    <div className="flex-1 min-w-0">
      <p className="text-xs text-green-800 font-medium mb-0.5">Can modify fields</p>
      <p className="text-xs text-green-700 truncate">
        {fields.slice(0, 3).map(f => f.displayName).join(', ')}
        {fields.length > 3 && ` +${fields.length - 3} more`}
      </p>
    </div>
  </div>
)

const ViewOnlyBadge: FunctionComponent = () => (
  <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg border border-gray-200">
    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
      />
    </svg>
    <span className="text-xs text-gray-600">View mode - suggestions only</span>
  </div>
)

const ContextInfo: FunctionComponent<{ context: AIConversationContext }> = ({ context }) => {
  const editableFields = context.availableFields.filter(f => f.isEditable)
  const focusedField = context.availableFields.find(f => f.isFocused)
  const isEditMode = context.viewMode === 'Edit' || context.viewMode === 'Create'

  return (
    <>
      {/* Current location */}
      <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-200 mb-2">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
        <span className="font-medium">{context.location}</span>
        {context.entityType && (
          <span className="text-gray-400 dark:text-gray-300">• {context.entityType}</span>
        )}
      </div>

      {/* Edit capabilities */}
      {isEditMode && editableFields.length > 0
        ? <EditCapabilities fields={editableFields} />
        : <ViewOnlyBadge />
      }

      {/* Focused field indicator */}
      {focusedField && (
        <div className="mt-2 flex items-center gap-1.5 text-xs text-blue-700">
          <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10.293 15.707a1 1 0 010-1.414L14.586 10l-4.293-4.293a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
            <path fillRule="evenodd" d="M4.293 15.707a1 1 0 010-1.414L8.586 10 4.293 5.707a1 1 0 011.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
          <span className="font-medium">Focused: {focusedField.displayName}</span>
        </div>
      )}
    </>
  )
}

// --- Main Component ---

export const ChatHeader: FunctionComponent<ChatHeaderProps> = ({
  context,
  onClose,
  onMinimize,
  onExpand,
  isMinimized,
  onSnapLeft,
  onSnapRight,
}) => {
  return (
    <div className="drag-handle p-4 border-b bg-gradient-to-r from-blue-50 to-purple-50 dark:from-slate-800 dark:to-slate-700 cursor-move">
      {/* Top row: Title and controls */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-lg bg-slate-900 dark:bg-slate-700 flex items-center justify-center overflow-visible">
            <WebsterIcon size={38} state="aware" />
          </div>
          <h3 className="text-sm font-semibold text-gray-800 dark:text-white">Webster</h3>
        </div>

        <WindowControls
          isMinimized={isMinimized}
          onMinimize={onMinimize}
          onExpand={onExpand}
          onClose={onClose}
          onSnapLeft={onSnapLeft}
          onSnapRight={onSnapRight}
        />
      </div>

      {/* Context information */}
      {context && <ContextInfo context={context} />}
    </div>
  )
}
